/**
 * MLinkOne PO Workbench Redux slice — Phase A (browser). Holds the canonical-PO list
 * view (rows + filters + paging + sort) and a per-PO drill-down cache. Kept standalone
 * (not folded into packAnalysisWorkbenchSlice) so PO and ASN state stay decoupled.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PoListItem, PoCanonicalResponse, PoIngestBatchResponse, PoRejectedItem } from '@/app/services/api/poWorkbenchService';
import { fetchPoList, fetchPoCanonical, ingestPoBatch, fetchRejectedPo, reprocessPo, PO_FETCH_ABORTED } from '../thunks/poWorkbenchThunks';

export interface PoBrowserFilters {
  // Buyer/supplier are driven by the top-bar active party (not manual code inputs); the
  // browse list filters by PO number + created-at range.
  poNumber: string;
  dateFrom: string | null;
  dateTo: string | null;
}

export interface PoWorkbenchState {
  browser: {
    list: PoListItem[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
    filters: PoBrowserFilters;
    sortBy: string | null;
    sortDir: 'asc' | 'desc' | null;
    isListLoading: boolean;
    listError: string | null;
    /** Drill-down cache keyed by poId. */
    detailById: Record<number, PoCanonicalResponse>;
    isDetailLoading: boolean;
    detailError: string | null;
  };
  ingest: {
    isUploading: boolean;
    error: string | null;
    lastResult: PoIngestBatchResponse | null;
  };
  rejected: {
    list: PoRejectedItem[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
    isLoading: boolean;
    error: string | null;
    /** Source-doc ids currently being reprocessed (for per-row spinners). */
    reprocessingIds: number[];
  };
}

const emptyFilters: PoBrowserFilters = {
  poNumber: '',
  dateFrom: null,
  dateTo: null,
};

const initialState: PoWorkbenchState = {
  browser: {
    list: [],
    totalCount: 0,
    pageIndex: 1,
    pageSize: 50,
    filters: { ...emptyFilters },
    sortBy: null,
    sortDir: null,
    isListLoading: false,
    listError: null,
    detailById: {},
    isDetailLoading: false,
    detailError: null,
  },
  ingest: {
    isUploading: false,
    error: null,
    lastResult: null,
  },
  rejected: {
    list: [],
    totalCount: 0,
    pageIndex: 1,
    pageSize: 50,
    isLoading: false,
    error: null,
    reprocessingIds: [],
  },
};

const poWorkbenchSlice = createSlice({
  name: 'poWorkbench',
  initialState,
  reducers: {
    setBrowserFilters(state, action: PayloadAction<Partial<PoBrowserFilters>>) {
      state.browser.filters = { ...state.browser.filters, ...action.payload };
      state.browser.pageIndex = 1; // filter change resets paging
    },
    clearBrowserFilters(state) {
      state.browser.filters = { ...emptyFilters };
      state.browser.pageIndex = 1;
    },
    setBrowserPage(state, action: PayloadAction<number>) {
      state.browser.pageIndex = Math.max(1, action.payload);
    },
    setBrowserPageSize(state, action: PayloadAction<number>) {
      state.browser.pageSize = action.payload;
      state.browser.pageIndex = 1;
    },
    setBrowserSort(state, action: PayloadAction<{ sortBy: string | null; sortDir: 'asc' | 'desc' | null }>) {
      state.browser.sortBy = action.payload.sortBy;
      state.browser.sortDir = action.payload.sortDir;
      state.browser.pageIndex = 1;
    },
    clearIngestResult(state) {
      state.ingest.lastResult = null;
      state.ingest.error = null;
    },
    /** Tenant switch — drop everything to avoid cross-tenant leakage. */
    resetPoWorkbench() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPoList.pending, (state) => {
        state.browser.isListLoading = true;
        state.browser.listError = null;
      })
      .addCase(fetchPoList.fulfilled, (state, action) => {
        state.browser.isListLoading = false;
        state.browser.list = action.payload.items;
        state.browser.totalCount = action.payload.totalCount;
        state.browser.pageIndex = action.payload.pageIndex;
        state.browser.pageSize = action.payload.pageSize;
      })
      .addCase(fetchPoList.rejected, (state, action) => {
        if (action.payload === PO_FETCH_ABORTED) return; // superseded/unmounted — ignore
        state.browser.isListLoading = false;
        state.browser.listError = (action.payload as string) ?? action.error.message ?? 'Failed to load PO list.';
      })
      .addCase(fetchPoCanonical.pending, (state) => {
        state.browser.isDetailLoading = true;
        state.browser.detailError = null;
      })
      .addCase(fetchPoCanonical.fulfilled, (state, action) => {
        state.browser.isDetailLoading = false;
        state.browser.detailById[action.payload.poId] = action.payload;
      })
      .addCase(fetchPoCanonical.rejected, (state, action) => {
        if (action.payload === PO_FETCH_ABORTED) return; // superseded/unmounted — ignore
        state.browser.isDetailLoading = false;
        state.browser.detailError = (action.payload as string) ?? action.error.message ?? 'Failed to load PO.';
      })
      .addCase(ingestPoBatch.pending, (state) => {
        state.ingest.isUploading = true;
        state.ingest.error = null;
      })
      .addCase(ingestPoBatch.fulfilled, (state, action) => {
        state.ingest.isUploading = false;
        state.ingest.lastResult = action.payload;
      })
      .addCase(ingestPoBatch.rejected, (state, action) => {
        if (action.payload === PO_FETCH_ABORTED) return;
        state.ingest.isUploading = false;
        state.ingest.error = (action.payload as string) ?? action.error.message ?? 'PO import failed.';
      })
      .addCase(fetchRejectedPo.pending, (state) => {
        state.rejected.isLoading = true;
        state.rejected.error = null;
      })
      .addCase(fetchRejectedPo.fulfilled, (state, action) => {
        state.rejected.isLoading = false;
        state.rejected.list = action.payload.items;
        state.rejected.totalCount = action.payload.totalCount;
        state.rejected.pageIndex = action.payload.pageIndex;
        state.rejected.pageSize = action.payload.pageSize;
      })
      .addCase(fetchRejectedPo.rejected, (state, action) => {
        if (action.payload === PO_FETCH_ABORTED) return;
        state.rejected.isLoading = false;
        state.rejected.error = (action.payload as string) ?? action.error.message ?? 'Failed to load rejected POs.';
      })
      .addCase(reprocessPo.pending, (state, action) => {
        state.rejected.reprocessingIds.push(action.meta.arg.sourceDocId);
      })
      .addCase(reprocessPo.fulfilled, (state, action) => {
        const id = action.meta.arg.sourceDocId;
        state.rejected.reprocessingIds = state.rejected.reprocessingIds.filter((x) => x !== id);
        // The original landing was consumed (marked reprocessed) → drop it from the list.
        state.rejected.list = state.rejected.list.filter((r) => r.sourceDocId !== id);
        state.rejected.totalCount = Math.max(0, state.rejected.totalCount - 1);
      })
      .addCase(reprocessPo.rejected, (state, action) => {
        const id = action.meta.arg.sourceDocId;
        state.rejected.reprocessingIds = state.rejected.reprocessingIds.filter((x) => x !== id);
        state.rejected.error = (action.payload as string) ?? action.error.message ?? 'Reprocess failed.';
      });
  },
});

export const {
  setBrowserFilters,
  clearBrowserFilters,
  setBrowserPage,
  setBrowserPageSize,
  setBrowserSort,
  clearIngestResult,
  resetPoWorkbench,
} = poWorkbenchSlice.actions;

export default poWorkbenchSlice.reducer;
